import { motion, useAnimationControls } from 'framer-motion';
import { Ticket, Image, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Button from '../components/Button';

const HomePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'event' | 'organization'>('event');
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const galleryImages = [
    "https://interfaces-cdn.zapier.com/8da60066-b07f-472b-a9c5-97e4120792e0/IMG_1846.JPG",
    "https://interfaces-cdn.zapier.com/297811e8-e403-408a-904e-65f97d21f44d/IMG_9689.jpeg",
    "https://interfaces-cdn.zapier.com/87a20825-7033-4dc2-981d-6b7445b629a2/IMG_4805.jpeg",
    "https://interfaces-cdn.zapier.com/85d0a0a4-0800-4985-a18b-55c71e2bd8d6/IMG_96882.jpeg",
    "https://interfaces-cdn.zapier.com/4924eb15-2963-4e7b-9475-60c890282641/IMG_4804.jpeg",
    "https://interfaces-cdn.zapier.com/d75ca825-08f8-4c2b-b720-233dc382f4fd/IMG_2799EC961E3E1.jpeg",
    "https://interfaces-cdn.zapier.com/97ebce53-e75f-471a-8111-60636e06d2fc/IMG_BFE889E8050B1.jpeg",
    "https://interfaces-cdn.zapier.com/8635962d-9eec-4f90-aac5-ae011ec69b61/horsepicture.jpg"
  ];

  const sponsorData = [
    { name: "Mason Machinery", logo: "https://interfaces-cdn.zapier.com/23a08929-3578-4acc-8bfd-4b998c76c26b/IMG_4830.JPEG" },
    { name: "JJWD Hardware", logo: "https://interfaces-cdn.zapier.com/3f86e4d9-23fb-4d19-96da-238307e07e9b/67f52dcc2dba2_jjwd_logo_W_WORDS.jpg" },
    { name: "Barretts", logo: "https://interfaces-cdn.zapier.com/fd196c86-f79c-4479-8a1d-a1d2059e396f/IMG_4831.JPEG" },
    { name: "The Parts House", logo: "https://interfaces-cdn.zapier.com/84c6b4d9-463c-4f26-b54c-49b2cc218062/images1.png" },
    { name: "Nelson's Sunbeam Coal", logo: "https://interfaces-cdn.zapier.com/f776b42a-e34b-4a2b-ac51-1ef7c26e4400/IMG_4829.JPEG" },
    { name: "SR Mechanical", logo: "https://interfaces-cdn.zapier.com/3b5da413-9a0a-4882-8167-e8b1b46e3c72/SRLogoWhiteBlack.png" },
    { name: "Robinson Transport", logo: "https://interfaces-cdn.zapier.com/487ee7b5-0009-4cb7-9bdc-b6d13fa46c7e/IMG_4832.JPEG" },
    { name: "Burns Saddlery", logo: "https://interfaces-cdn.zapier.com/6793d86f-727e-4e05-96dd-04adbad5120c/images2.png" },
    { name: "Redmond Minerals", logo: "https://interfaces-cdn.zapier.com/31654854-65d3-416f-b658-8ce77b696ff7/IMG_4833.JPEG" },
    { name: "Rasmussen Excavation", logo: "https://interfaces-cdn.zapier.com/8b03c35d-d3a9-48f4-810f-0f41c96db559/IMG_4828.JPEG" }
  ];

  // Create a duplicated array for infinite scrolling effect
  const allSponsors = [...sponsorData, ...sponsorData];

  useEffect(() => {
    const startMarquee = async () => {
      if (carouselRef.current) {
        // First set initial position
        await controls.start({
          x: 0,
          transition: { duration: 0 }
        });
        
        // Then animate to the negative width of one set of logos
        await controls.start({
          x: `-${sponsorData.length * 200}px`,
          transition: {
            duration: 40,
            ease: "linear",
            repeat: Infinity,
          }
        });
      }
    };
    
    startMarquee();
  }, [controls, sponsorData.length]);

  const handleTicketClick = () => {
    window.open("https://www.rodeoticket.com/rodeos/salina-independence-day-rodeo/2026/tickets", "_blank");
  };

  return (
    <div className="min-h-screen-content">
      {/* Hero section with rodeo information */}
      <section className="relative mb-16 overflow-hidden rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6 py-16">
          {/* Title and description */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Salina Independence Day Rodeo
            </h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 text-center mb-6 max-w-3xl mx-auto"
            >
              76 Years of Hometown, Patriotic Rodeo Fun
            </motion.p>

            {/* Venue and time information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center mb-8"
            >
              <p className="text-2xl md:text-3xl font-bold text-white mb-2">
                Salina Outdoor Arena
              </p>
              <p className="text-xl md:text-2xl font-bold text-white">
                7:45 PM on July 3rd and 4th
              </p>
            </motion.div>
          </motion.div>

          {/* Entries Closed Announcement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-8"
          >
            <div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                Contestant Entries Have Been Closed!
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-3">
                As of Tuesday June 30th at 11 pm, no more entries are being accepted for the rodeo.
              </p>
              <p className="text-xl md:text-2xl font-semibold text-white">
                See you this weekend, happy 4th of July!
              </p>
            </div>
          </motion.div>
          
          {/* Navigation buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={handleTicketClick}
            >
              Buy Tickets
            </Button>
            {/* Contestant entries closed
            <Button
              variant="primary"
              size="lg"
              icon={<UserPlus size={18} />}
              onClick={() => navigate('/contestant-registration')}
            >
              Contestant Sign Up
            </Button>
            */}
            <Button
              variant="primary"
              size="lg"
              icon={<Image size={18} />}
              onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Photo Gallery
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Single ticket purchase option */}
      <section id="tickets" className="py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-4">
            Get Your Tickets
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Purchase your tickets for an unforgettable rodeo experience
          </p>
        </div>

        <motion.div
          className="max-w-md mx-auto"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={item}>
            <div
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow h-full flex flex-col"
            >
              <div className="bg-secondary-50 p-6 flex justify-center">
                <div className="bg-secondary-100 p-4 rounded-full">
                  <Ticket size={32} className="text-secondary-700" />
                </div>
              </div>
              <div className="p-6 flex-grow text-center">
                <h3 className="text-xl font-heading font-semibold text-secondary-800 mb-3">
                  Buy Tickets
                </h3>
                <p className="text-gray-600 mb-2">
                  Get tickets to attend the rodeo
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  Click on Purchase Tickets to see pricing.
                </p>
                <Button
                  variant="secondary"
                  fullWidth
                  className="hover:bg-green-600 hover:border-green-600 transition-colors duration-200"
                  onClick={handleTicketClick}
                >
                  Purchase Tickets
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Contestant entries closed
          <motion.div variants={item}>
            <div
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow h-full flex flex-col"
            >
              <div className="bg-primary-50 p-6 flex justify-center">
                <div className="bg-primary-100 p-4 rounded-full">
                  <UserPlus size={32} className="text-primary-700" />
                </div>
              </div>
              <div className="p-6 flex-grow text-center">
                <h3 className="text-xl font-heading font-semibold text-primary-800 mb-3">
                  Contestant Sign Up
                </h3>
                <p className="text-gray-600 mb-2">
                  Register to compete in the rodeo
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  Sign up for barrel racing, bull riding, team roping, and more.
                </p>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => navigate('/contestant-registration')}
                >
                  Register as Contestant
                </Button>
              </div>
            </div>
          </motion.div>
          */}
        </motion.div>
      </section>

      {/* Sponsor Section with names under logos */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-heading font-bold text-primary-800 mb-8 text-center">
            Our Sponsors
          </h2>
          
          <div className="relative overflow-hidden">
            <div className="w-full">
              <div className="overflow-hidden">
                <motion.div 
                  ref={carouselRef}
                  className="flex"
                  animate={controls}
                  style={{ width: `${allSponsors.length * 200}px` }}
                >
                  {allSponsors.map((sponsor, index) => (
                    <div 
                      key={index} 
                      className="flex-shrink-0 w-[200px] h-[160px] px-4 flex flex-col items-center justify-center"
                    >
                      <div className="h-[100px] flex items-center justify-center mb-2">
                        <img 
                          src={sponsor.logo} 
                          alt={`${sponsor.name} logo`} 
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <p className="text-xs text-gray-700 text-center font-medium">{sponsor.name}</p>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section id="gallery" className="py-12 bg-gray-50 rounded-xl my-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-800 mb-4">
                Photo Gallery
              </h2>
              <p className="text-gray-600 max-w-2xl mb-6 md:mb-0">
                Check out photos from our previous rodeo events. Relive the excitement and see what makes the Salina Independence Day Rodeo special.
              </p>
            </div>
            <Button
              variant="outline"
              icon={<Image size={18} />}
              onClick={() => navigate('/photo-gallery')}
            >
              View All Photos
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.slice(0, 4).map((image, index) => (
              <div 
                key={index}
                className="aspect-square rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate('/photo-gallery')}
              >
                <img 
                  src={image} 
                  alt={`Rodeo gallery preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabbed information section */}
      <section className="py-12 bg-primary-50 rounded-xl mt-16 mb-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Tab navigation */}
          <div className="flex border-b border-gray-300 mb-6">
            <button
              className={`px-4 py-2 font-medium text-sm flex items-center ${
                activeTab === 'event'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-primary-600'
              }`}
              onClick={() => setActiveTab('event')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Event Details
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm flex items-center ${
                activeTab === 'organization'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-primary-600'
              }`}
              onClick={() => setActiveTab('organization')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              About the Organization
            </button>
          </div>
          
          {/* Event Details Tab */}
          {activeTab === 'event' && (
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-900 mb-6 text-center">
                Event Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-primary-800 mb-3">
                    Location & Date
                  </h3>
                  <div className="space-y-2">
                    <p className="text-gray-700">
                      <strong>Thursday July 3rd</strong>
                    </p>
                    <p className="text-gray-700">
                      <strong>Friday July 4th</strong>
                    </p>
                    <p className="text-gray-700">
                      <strong>7:45 PM</strong>
                    </p>
                    <p className="text-gray-700">
                      <strong>SALINA OUTDOOR ARENA</strong>
                    </p>
                    <p className="text-gray-700">
                      700 East Main Street, Salina
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-primary-800 mb-3">
                    Events
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Bareback Riding</li>
                    <li>Steer Wrestling</li>
                    <li>Team Roping</li>
                    <li>Breakaway Roping</li>
                    <li>Saddle Bronc</li>
                    <li>Tie-Down Roping</li>
                    <li>Barrel Racing</li>
                    <li>Bull Riding</li>
                    <li>Ranch Bronc Riding</li>
                    <li>Jr. Barrels</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* About the Organization Tab */}
          {activeTab === 'organization' && (
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-900 mb-6 text-center">
                About the Organization
              </h2>
              
              <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
                <div className="flex items-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-600 mr-2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <h3 className="text-lg font-semibold text-primary-800">2026 Salina Riding Club Officers</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong>President:</strong> Jeff Reese
                    </p>
                    <p className="text-gray-700 mb-2">
                      <strong>Vice President:</strong> Gavin Stewart
                    </p>
                    <p className="text-gray-700 mb-2">
                      <strong>Secretary:</strong> Andrea Andreason
                    </p>
                    <p className="text-gray-700">
                      <strong>Treasurer:</strong> Scott Reeves
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong>Rodeo Chairman:</strong> Wes Andreason
                    </p>
                    <p className="text-gray-700">
                      <strong>Co Rodeo Chairman:</strong> Hayden Smith
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a 
                  href="https://www.facebook.com/salinaindependencedayrodeo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Visit our Facebook Page
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;